import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LineElement,
    LinearScale,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js'
import { Bar, Doughnut, Line } from 'react-chartjs-2'
import { FaChevronDown, FaChevronRight, FaSyncAlt } from 'react-icons/fa'
import api from '../../../helpers/apiRequester'
import styles from './styles.module.scss'
import {
    BASE_URL,
    DEFAULT_LINKED_FILTERS,
    LINKED_STATUS_LABELS,
    LINKED_EXPORT_SCOPE_LABELS,
    LinkedFundingClientRow,
    LinkedFundingExportScope,
    LinkedFundingFilters,
    LinkedFundingStatus,
    LinkedFundingSummaryResponse,
    RANGE_PRESETS,
    REMOVAL_REASON_LABELS,
    buildLinkedExportCsv,
    downloadCsvFile,
    formatDateTime,
    formatDay,
    linkedFiltersEqual,
} from './shared'

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
)

const PAGE_SIZE = 25
const SUMMARY_PATH = '/api/ffj-linked-funding-summary'
const CLIENTS_PATH = '/api/ffj-linked-funding-clients'
const DETAIL_PATH = '/api/ffj-linked-funding-client-detail'
const EXPORT_PATH = '/api/ffj-linked-funding-export'
const HIGH_CLAIM_OPEN_KEY = 'ffjLinkedFunding.highClaimOpen'

const statusClass = (status: LinkedFundingStatus): string => {
    if (status === 'in_batch') return styles.linkedStatusGood
    if (status === 'audit_removed') return styles.linkedStatusBad
    return styles.linkedStatusMuted
}

const filtersFromParams = (params: URLSearchParams): Partial<LinkedFundingFilters> => {
    const patch: Partial<LinkedFundingFilters> = {}
    const status = params.get('status')
    if (status === 'in_batch' || status === 'audit_removed' || status === 'never_batched') {
        patch.status = status
    }
    const removalReason = params.get('removalReason')
    if (removalReason) patch.removalReason = removalReason
    if (params.get('highClaimCount') === '1' || params.get('highClaimCount') === 'true') {
        patch.highClaimCount = true
    }
    const ur = params.get('ur')
    if (ur) patch.ur = ur
    const batchGroup = params.get('batchGroup')
    if (batchGroup) patch.batchGroup = batchGroup
    const dateFrom = params.get('dateFrom')
    if (dateFrom) patch.dateFrom = dateFrom
    const dateTo = params.get('dateTo')
    if (dateTo) patch.dateTo = dateTo
    return patch
}

export const LinkedFundingTab = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const initialPatch = useMemo(() => filtersFromParams(searchParams), []) // eslint-disable-line react-hooks/exhaustive-deps

    const [draft, setDraft] = useState<LinkedFundingFilters>({
        ...DEFAULT_LINKED_FILTERS,
        ...initialPatch,
    })
    const [applied, setApplied] = useState<LinkedFundingFilters>({
        ...DEFAULT_LINKED_FILTERS,
        ...initialPatch,
    })
    const [page, setPage] = useState(1)
    const [summary, setSummary] = useState<LinkedFundingSummaryResponse | null>(null)
    const [summaryLoading, setSummaryLoading] = useState(false)
    const [rows, setRows] = useState<LinkedFundingClientRow[]>([])
    const [total, setTotal] = useState(0)
    const [listLoading, setListLoading] = useState(false)
    const [listError, setListError] = useState<string | null>(null)
    const [refreshNonce, setRefreshNonce] = useState(0)
    const [highClaimOpen, setHighClaimOpen] = useState(
        () => window.localStorage.getItem(HIGH_CLAIM_OPEN_KEY) === 'open'
    )
    const [detailClientId, setDetailClientId] = useState<number | null>(() => {
        const raw = searchParams.get('clientId')
        const n = raw ? Number(raw) : NaN
        return Number.isFinite(n) && n > 0 ? n : null
    })
    const [detail, setDetail] = useState<any | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)
    const [exportingScope, setExportingScope] = useState<LinkedFundingExportScope | null>(null)
    const [exportError, setExportError] = useState<string | null>(null)

    const hasPendingChanges = !linkedFiltersEqual(draft, applied)

    const toggleHighClaim = useCallback(() => {
        setHighClaimOpen((open) => {
            window.localStorage.setItem(HIGH_CLAIM_OPEN_KEY, open ? 'closed' : 'open')
            return !open
        })
    }, [])

    const patchDraft = useCallback((patch: Partial<LinkedFundingFilters>) => {
        setDraft((current) => ({ ...current, ...patch }))
    }, [])

    const applyFilters = useCallback(
        (patch?: Partial<LinkedFundingFilters>) => {
            const next = { ...draft, ...patch }
            setDraft(next)
            setApplied(next)
            setPage(1)
            const params = new URLSearchParams()
            if (next.status) params.set('status', next.status)
            if (next.removalReason) params.set('removalReason', next.removalReason)
            if (next.highClaimCount) params.set('highClaimCount', '1')
            if (next.ur) params.set('ur', next.ur)
            if (next.batchGroup) params.set('batchGroup', next.batchGroup)
            if (next.dateFrom) params.set('dateFrom', next.dateFrom)
            if (next.dateTo) params.set('dateTo', next.dateTo)
            if (detailClientId) params.set('clientId', String(detailClientId))
            setSearchParams(params)
        },
        [draft, detailClientId, setSearchParams]
    )

    const resetFilters = useCallback(() => {
        setDraft({ ...DEFAULT_LINKED_FILTERS })
        setApplied({ ...DEFAULT_LINKED_FILTERS })
        setPage(1)
        setDetailClientId(null)
        setDetail(null)
        setSearchParams({})
    }, [setSearchParams])

    const isLoading = summaryLoading || listLoading

    const filterBody = useMemo(
        () => ({
            search: applied.search.trim() || undefined,
            dateFrom: applied.dateFrom || undefined,
            dateTo: applied.dateTo || undefined,
            ur: applied.ur || undefined,
            batchGroup: applied.batchGroup || undefined,
            status: applied.status || undefined,
            removalReason: applied.removalReason || undefined,
            highClaimCount: applied.highClaimCount || undefined,
            includeTest: applied.includeTest,
            sort: applied.sort,
        }),
        [applied]
    )

    const downloadMetric = useCallback(
        async (scope: LinkedFundingExportScope) => {
            setExportingScope(scope)
            setExportError(null)
            try {
                const response = await api.post(`${BASE_URL}${EXPORT_PATH}`, {
                    ...filterBody,
                    scope,
                })
                if (!response.data?.success) {
                    throw new Error(response.data?.message || 'Export failed')
                }
                const rows = (response.data.rows || []) as Record<string, unknown>[]
                if (!rows.length) {
                    setExportError(`No clients to download for ${LINKED_EXPORT_SCOPE_LABELS[scope]}.`)
                    return
                }
                const csv = buildLinkedExportCsv(rows, scope)
                const day = new Date().toISOString().slice(0, 10)
                downloadCsvFile(csv, `ffj-linked-funding-${scope}-${day}.csv`)
                if (response.data.truncated) {
                    setExportError(
                        `Downloaded first ${rows.length} rows — export was truncated at the server limit.`
                    )
                }
            } catch (err: any) {
                setExportError(err?.response?.data?.message || err?.message || 'Export failed')
            } finally {
                setExportingScope(null)
            }
        },
        [filterBody]
    )

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            setSummaryLoading(true)
            try {
                const response = await api.post(`${BASE_URL}${SUMMARY_PATH}`, filterBody)
                if (cancelled) return
                setSummary(response.data?.success ? response.data : null)
            } catch {
                if (!cancelled) setSummary(null)
            } finally {
                if (!cancelled) setSummaryLoading(false)
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [filterBody, refreshNonce])

    useEffect(() => {
        let cancelled = false
        const run = async () => {
            setListLoading(true)
            setListError(null)
            try {
                const response = await api.post(`${BASE_URL}${CLIENTS_PATH}`, {
                    ...filterBody,
                    page,
                    pageSize: PAGE_SIZE,
                })
                if (cancelled) return
                if (!response.data?.success) throw new Error(response.data?.message || 'List failed')
                setRows(response.data.rows || [])
                setTotal(response.data.total || 0)
            } catch (error: any) {
                if (!cancelled) {
                    setListError(error?.response?.data?.message || error?.message || 'Could not load clients')
                    setRows([])
                    setTotal(0)
                }
            } finally {
                if (!cancelled) setListLoading(false)
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [filterBody, page, refreshNonce])

    useEffect(() => {
        if (!detailClientId) {
            setDetail(null)
            return
        }
        let cancelled = false
        const run = async () => {
            setDetailLoading(true)
            try {
                const response = await api.post(`${BASE_URL}${DETAIL_PATH}`, { clientId: detailClientId })
                if (cancelled) return
                setDetail(response.data?.success ? response.data : { error: response.data?.message || 'Failed' })
            } catch (error: any) {
                if (!cancelled) {
                    setDetail({
                        error: error?.response?.data?.message || error?.message || 'Could not load client',
                    })
                }
            } finally {
                if (!cancelled) setDetailLoading(false)
            }
        }
        run()
        return () => {
            cancelled = true
        }
    }, [detailClientId, refreshNonce])

    const openClient = (clientId: number) => {
        setDetailClientId(clientId)
        const params = new URLSearchParams(searchParams)
        params.set('clientId', String(clientId))
        setSearchParams(params)
    }

    const closeClient = () => {
        setDetailClientId(null)
        setDetail(null)
        const params = new URLSearchParams(searchParams)
        params.delete('clientId')
        setSearchParams(params)
    }

    const totals = summary?.summary.totals
    const excessive = summary?.summary.excessive_claims
    const highClaim = summary?.summary.high_claim_clients
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    const statusChart = useMemo(() => {
        const t = totals
        return {
            labels: ['In batch', 'Audit-removed', 'Never batched'],
            datasets: [
                {
                    data: [t?.in_batch ?? 0, t?.audit_removed ?? 0, t?.never_batched ?? 0],
                    backgroundColor: ['#2e7d32', '#c62828', '#94a3b8'],
                    borderWidth: 0,
                },
            ],
        }
    }, [totals])

    const reasonsChart = useMemo(() => {
        const reasons = summary?.summary.removal_reasons ?? []
        return {
            labels: reasons.map((r) => r.label),
            datasets: [
                {
                    label: 'Clients',
                    data: reasons.map((r) => r.count),
                    backgroundColor: '#c62828',
                    borderRadius: 4,
                },
            ],
        }
    }, [summary])

    const dailyChart = useMemo(() => {
        const points = summary?.daily ?? []
        return {
            data: {
                labels: points.map((p) => p.day),
                datasets: [
                    {
                        label: 'Accepted via API',
                        data: points.map((p) => p.accepted),
                        borderColor: '#64748b',
                        backgroundColor: 'rgba(100, 116, 139, 0.1)',
                        fill: true,
                        tension: 0.3,
                    },
                    {
                        label: 'In batch',
                        data: points.map((p) => p.in_batch),
                        borderColor: '#2e7d32',
                        backgroundColor: 'rgba(46, 125, 50, 0.1)',
                        fill: true,
                        tension: 0.3,
                    },
                    {
                        label: 'Audit-removed',
                        data: points.map((p) => p.audit_removed),
                        borderColor: '#c62828',
                        backgroundColor: 'rgba(198, 40, 40, 0.08)',
                        fill: true,
                        tension: 0.3,
                    },
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                plugins: {
                    legend: { position: 'bottom' as const, labels: { boxWidth: 12, usePointStyle: true } },
                    tooltip: {
                        callbacks: {
                            title: (items: any[]) => (items[0] ? formatDay(String(items[0].label)) : ''),
                        },
                    },
                },
                scales: {
                    x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
                    y: { beginAtZero: true, ticks: { precision: 0 } },
                },
            },
        }
    }, [summary])

    const batchOptions = useMemo(() => {
        const batches = summary?.options.batches ?? []
        if (!draft.ur) return batches
        return batches.filter((b) => (b.ur || '').toLowerCase() === draft.ur.toLowerCase())
    }, [summary, draft.ur])

    return (
        <div className={styles.linkedFundingRoot}>
            {isLoading && (
                <div className={styles.linkedLoadingOverlay} role="status" aria-live="polite">
                    <div className={styles.linkedLoadingCard}>
                        <FaSyncAlt className={styles.refreshSpinning} aria-hidden="true" />
                        <div>
                            <strong>Loading linked funding data…</strong>
                            <p className={styles.footnote} style={{ margin: '4px 0 0' }}>
                                Matching accepted API clients to funding batches. This can take a moment.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.linkedPageHeader}>
                <p className={styles.footnote} style={{ margin: 0 }}>
                    Every accepted FFJ API client, linked to funding-monitor batch membership. Status is
                    derived from soft-removals only — hard-deleted funder rows leave no trail. Gate diagnosis for
                    never-batched clients is a later follow-on.
                </p>
                <div className={styles.linkedPageActions}>
                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={resetFilters}
                        disabled={isLoading}
                        title="Clear all filters and show every accepted API client"
                    >
                        Reset to all data
                    </button>
                    <button
                        type="button"
                        className={styles.refreshButton}
                        onClick={() => setRefreshNonce((n) => n + 1)}
                        disabled={isLoading}
                    >
                        <FaSyncAlt className={isLoading ? styles.refreshSpinning : undefined} aria-hidden="true" />
                        Refresh
                    </button>
                </div>
            </div>

            <div className={styles.statCards}>
                {(
                    [
                        {
                            scope: 'accepted' as const,
                            value: totals?.accepted_api_clients,
                            label: 'Accepted API clients',
                            valueClass: undefined as string | undefined,
                        },
                        {
                            scope: 'in_batch' as const,
                            value: totals?.in_batch,
                            label: 'In a funding batch',
                            valueClass: styles.statGood,
                        },
                        {
                            scope: 'audit_removed' as const,
                            value: totals?.audit_removed,
                            label: 'Audit-removed',
                            valueClass: styles.statBad,
                        },
                        {
                            scope: 'never_batched' as const,
                            value: totals?.never_batched,
                            label: 'Never batched',
                            valueClass: undefined as string | undefined,
                        },
                        {
                            scope: 'never_batched_eligible' as const,
                            value: totals?.never_batched_eligible,
                            label: 'Never batched — eligible',
                            valueClass: styles.statGood,
                        },
                    ] as const
                ).map((card) => (
                    <div key={card.scope} className={styles.statCard}>
                        <span className={`${styles.statValue}${card.valueClass ? ` ${card.valueClass}` : ''}`}>
                            {card.value ?? '—'}
                        </span>
                        <span className={styles.statLabel}>{card.label}</span>
                        <button
                            type="button"
                            className={styles.statDownloadButton}
                            onClick={() => downloadMetric(card.scope)}
                            disabled={
                                summaryLoading ||
                                exportingScope != null ||
                                card.value == null ||
                                card.value === 0
                            }
                            title={`Download CSV of ${card.label.toLowerCase()} (current filters)`}
                        >
                            {exportingScope === card.scope ? 'Preparing…' : 'Download CSV'}
                        </button>
                    </div>
                ))}
                <div className={styles.statCard}>
                    <span className={styles.statValue}>
                        {totals?.placement_rate != null ? `${totals.placement_rate}%` : '—'}
                    </span>
                    <span className={styles.statLabel}>Placement rate (still active)</span>
                </div>
            </div>
            {exportError && <p className={styles.errorText}>{exportError}</p>}
            <p className={styles.footnote} style={{ marginTop: '-8px', marginBottom: '18px' }}>
                Downloads respect the filters above. Accepted / in-batch CSVs are client data only;
                never-batched, never-batched eligible, and audit-removed CSVs also include eligibility
                and funding-outcome reasons.
            </p>

            <div className={styles.linkedHighClaimBanner}>
                <button
                    type="button"
                    className={styles.collapseHeader}
                    onClick={toggleHighClaim}
                    aria-expanded={highClaimOpen}
                >
                    {highClaimOpen ? (
                        <FaChevronDown aria-hidden="true" />
                    ) : (
                        <FaChevronRight aria-hidden="true" />
                    )}
                    <h4>
                        {highClaim?.total != null
                            ? `${highClaim.total} client${highClaim.total === 1 ? '' : 's'} with 10+ claims`
                            : 'Clients with 10+ claims'}
                    </h4>
                    {!highClaimOpen && highClaim && (
                        <span className={styles.collapseMeta}>
                            {highClaim.in_batch} in batch · {highClaim.audit_removed} removed
                        </span>
                    )}
                    <span className={styles.collapseHint}>{highClaimOpen ? 'Hide' : 'Show'}</span>
                </button>

                {highClaimOpen && (
                    <>
                        <div className={styles.linkedHighClaimMain}>
                            <p className={styles.footnote} style={{ margin: 0 }}>
                                These clients are accepted by the API and are fully eligible, but the auditor
                                cannot take a client with more than 9 claims. They often enter a funding batch
                                and are removed later (reason: more than 9 claims). Count is based on introducer
                                claims on the client file.
                            </p>
                            <button
                                type="button"
                                className={
                                    applied.highClaimCount ? styles.presetChipActive : styles.applyButton
                                }
                                onClick={() =>
                                    applyFilters({
                                        highClaimCount: true,
                                        removalReason: '',
                                        status: '',
                                    })
                                }
                            >
                                Show 10+ claim clients
                            </button>
                        </div>
                        <div className={styles.linkedHighClaimStats}>
                            <div>
                                <span className={styles.statValue}>{highClaim?.total ?? '—'}</span>
                                <span className={styles.statLabel}>Total with 10+</span>
                            </div>
                            <div>
                                <span className={`${styles.statValue} ${styles.statGood}`}>
                                    {highClaim?.in_batch ?? '—'}
                                </span>
                                <span className={styles.statLabel}>Still in a batch</span>
                            </div>
                            <div>
                                <span className={`${styles.statValue} ${styles.statBad}`}>
                                    {highClaim?.audit_removed ?? '—'}
                                </span>
                                <span className={styles.statLabel}>Audit-removed</span>
                            </div>
                            <div>
                                <span className={styles.statValue}>{highClaim?.never_batched ?? '—'}</span>
                                <span className={styles.statLabel}>Never batched</span>
                            </div>
                            <div
                                className={styles.linkedSpotlightInline}
                                role="button"
                                tabIndex={0}
                                title="Filter to clients explicitly removed with the excessive-claims reason"
                                onClick={() =>
                                    applyFilters({
                                        status: 'audit_removed',
                                        removalReason: 'excessive-claims-removal',
                                        highClaimCount: false,
                                    })
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        applyFilters({
                                            status: 'audit_removed',
                                            removalReason: 'excessive-claims-removal',
                                            highClaimCount: false,
                                        })
                                    }
                                }}
                            >
                                <span className={`${styles.statValue} ${styles.statBad}`}>
                                    {excessive?.removed_clients ?? highClaim?.removed_for_excessive ?? '—'}
                                </span>
                                <span className={styles.statLabel}>Tagged excessive-claims removal</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            <div className={styles.linkedChartsRow}>
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <h4>Status mix</h4>
                        <span className={styles.footnote}>{summaryLoading ? 'Calculating…' : 'Client counts'}</span>
                    </div>
                    <div className={styles.linkedDoughnutWrap}>
                        <Doughnut
                            data={statusChart}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        position: 'bottom',
                                        labels: { boxWidth: 12, usePointStyle: true },
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
                <div className={styles.chartCard}>
                    <div className={styles.chartHeader}>
                        <h4>Audit removal reasons</h4>
                        <span className={styles.footnote}>Only among audit-removed clients</span>
                    </div>
                    <div className={styles.chartWrap}>
                        {(summary?.summary.removal_reasons.length ?? 0) > 0 ? (
                            <Bar
                                data={reasonsChart}
                                options={{
                                    responsive: true,
                                    maintainAspectRatio: false,
                                    indexAxis: 'y',
                                    plugins: { legend: { display: false } },
                                    scales: {
                                        x: { beginAtZero: true, ticks: { precision: 0 } },
                                        y: { grid: { display: false } },
                                    },
                                }}
                            />
                        ) : (
                            <p className={styles.footnote}>No audit-removed clients in this filter set.</p>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.resultSummaryCard}>
                <div className={styles.chartHeader}>
                    <h4>Accepted vs placed over time</h4>
                    <span className={styles.footnote}>By first API acceptance day</span>
                </div>
                {(summary?.daily.length ?? 0) > 0 ? (
                    <div className={styles.chartWrap}>
                        <Line data={dailyChart.data} options={dailyChart.options} />
                    </div>
                ) : (
                    <p className={styles.footnote}>No daily series for this filter set.</p>
                )}
            </div>

            <div className={styles.linkedTablesRow}>
                <div className={styles.reasonsCard}>
                    <h4>By UR</h4>
                    <p className={styles.footnote} style={{ marginTop: 0 }}>
                        Rolled up by <code>nera_id</code> (e.g. ur11). One UR may cover several FC_ batches — client
                        counts here can include the same person once per batch they appear in. Click a row to filter.
                    </p>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>UR</th>
                                <th>Batch</th>
                                <th>Clients</th>
                                <th>Active</th>
                                <th>Removed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary?.by_ur ?? []).length === 0 ? (
                                <tr>
                                    <td colSpan={5}>No batch membership in this filter set.</td>
                                </tr>
                            ) : (
                                (summary?.by_ur ?? []).map((row) => (
                                    <tr
                                        key={`${row.ur}-${row.batch_group}-${row.batches}`}
                                        className={styles.linkedClickRow}
                                        onClick={() =>
                                            applyFilters({
                                                ur: row.ur || '',
                                                batchGroup: row.batch_group || '',
                                            })
                                        }
                                    >
                                        <td>{row.ur || '—'}</td>
                                        <td>
                                            {row.batch_group ||
                                                (row.batches > 1 ? `${row.batches} batches` : '—')}
                                        </td>
                                        <td>{row.clients}</td>
                                        <td>{row.in_batch}</td>
                                        <td>{row.audit_removed}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                <div className={styles.reasonsCard}>
                    <h4>By funding batch</h4>
                    <p className={styles.footnote} style={{ marginTop: 0 }}>
                        One row per <code>batch_group</code> (e.g. FC_20260731_psig). Use this when you need the
                        exact batch, not the UR roll-up. Click a row to filter.
                    </p>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Batch</th>
                                <th>UR</th>
                                <th>Clients</th>
                                <th>Active</th>
                                <th>Removed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(summary?.by_batch ?? []).length === 0 ? (
                                <tr>
                                    <td colSpan={5}>No batches in this filter set.</td>
                                </tr>
                            ) : (
                                (summary?.by_batch ?? []).map((row) => (
                                    <tr
                                        key={row.batch_group}
                                        className={styles.linkedClickRow}
                                        onClick={() =>
                                            applyFilters({
                                                batchGroup: row.batch_group,
                                                ur: row.ur || '',
                                            })
                                        }
                                    >
                                        <td>{row.batch_group}</td>
                                        <td>{row.ur || '—'}</td>
                                        <td>{row.clients}</td>
                                        <td>{row.in_batch}</td>
                                        <td>{row.audit_removed}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className={styles.filterPanel}>
                <div className={styles.filterPanelHeader}>
                    <h4>Filter linked clients</h4>
                    <span className={styles.footnote}>
                        Apply to refresh the tiles, charts and explorer together.
                    </span>
                </div>

                <div className={styles.presetRow}>
                    {RANGE_PRESETS.map((preset) => {
                        const range = preset.resolve()
                        const active = draft.dateFrom === range.dateFrom && draft.dateTo === range.dateTo
                        return (
                            <button
                                key={preset.id}
                                type="button"
                                className={active ? styles.presetChipActive : styles.presetChip}
                                onClick={() => patchDraft(range)}
                            >
                                {preset.label}
                            </button>
                        )
                    })}
                    <button
                        type="button"
                        className={styles.presetChip}
                        onClick={() => patchDraft({ dateFrom: '', dateTo: '' })}
                    >
                        Clear dates
                    </button>
                </div>

                <div className={styles.filterGrid}>
                    <label className={styles.filterField}>
                        <span>From</span>
                        <input
                            type="date"
                            value={draft.dateFrom}
                            onChange={(e) => patchDraft({ dateFrom: e.target.value })}
                        />
                    </label>
                    <label className={styles.filterField}>
                        <span>To</span>
                        <input
                            type="date"
                            value={draft.dateTo}
                            onChange={(e) => patchDraft({ dateTo: e.target.value })}
                        />
                    </label>
                    <label className={styles.filterField}>
                        <span>UR</span>
                        <select value={draft.ur} onChange={(e) => patchDraft({ ur: e.target.value, batchGroup: '' })}>
                            <option value="">All URs</option>
                            {(summary?.options.urs ?? []).map((ur) => (
                                <option key={ur} value={ur}>
                                    {ur}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className={styles.filterField}>
                        <span>Funding batch</span>
                        <select
                            value={draft.batchGroup}
                            onChange={(e) => patchDraft({ batchGroup: e.target.value })}
                        >
                            <option value="">All batches</option>
                            {batchOptions.map((b) => (
                                <option key={b.batch_group} value={b.batch_group}>
                                    {b.batch_group}
                                    {b.ur ? ` (${b.ur})` : ''}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className={styles.filterField}>
                        <span>Status</span>
                        <select
                            value={draft.status}
                            onChange={(e) =>
                                patchDraft({ status: e.target.value as LinkedFundingFilters['status'] })
                            }
                        >
                            <option value="">All statuses</option>
                            <option value="in_batch">{LINKED_STATUS_LABELS.in_batch}</option>
                            <option value="audit_removed">{LINKED_STATUS_LABELS.audit_removed}</option>
                            <option value="never_batched">{LINKED_STATUS_LABELS.never_batched}</option>
                        </select>
                    </label>
                    <label className={styles.filterField}>
                        <span>Removal reason</span>
                        <select
                            value={draft.removalReason}
                            onChange={(e) => patchDraft({ removalReason: e.target.value })}
                        >
                            <option value="">Any / n/a</option>
                            {Object.entries(REMOVAL_REASON_LABELS).map(([id, label]) => (
                                <option key={id} value={id}>
                                    {label}
                                </option>
                            ))}
                            <option value="__none__">No reason recorded</option>
                        </select>
                    </label>
                    <label className={`${styles.filterField} ${styles.filterFieldWide}`}>
                        <span>Search</span>
                        <input
                            placeholder="Name, email, client id, client ref, batch, UR…"
                            value={draft.search}
                            onChange={(e) => patchDraft({ search: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') applyFilters()
                            }}
                        />
                    </label>
                    <label className={styles.filterField}>
                        <span>Sort</span>
                        <select
                            value={draft.sort}
                            onChange={(e) =>
                                patchDraft({ sort: e.target.value as LinkedFundingFilters['sort'] })
                            }
                        >
                            <option value="newest">Newest first</option>
                            <option value="oldest">Oldest first</option>
                        </select>
                    </label>
                    <div className={styles.filterToggles}>
                        <label>
                            <input
                                type="checkbox"
                                checked={draft.includeTest}
                                onChange={(e) => patchDraft({ includeTest: e.target.checked })}
                            />
                            Include test deliveries
                        </label>
                        <label>
                            <input
                                type="checkbox"
                                checked={draft.highClaimCount}
                                onChange={(e) => patchDraft({ highClaimCount: e.target.checked })}
                            />
                            Only clients with 10+ claims
                        </label>
                    </div>
                </div>

                <div className={styles.filterActions}>
                    <span className={hasPendingChanges ? styles.pendingHint : styles.appliedHint}>
                        {hasPendingChanges
                            ? 'Filters changed — not applied yet'
                            : `${total} client${total === 1 ? '' : 's'} match`}
                    </span>
                    <div className={styles.filterButtons}>
                        <button type="button" className={styles.applyButton} onClick={() => applyFilters()}>
                            Apply filters
                        </button>
                        <button type="button" className={styles.secondaryButton} onClick={resetFilters}>
                            Reset
                        </button>
                        <button
                            className={styles.refreshButton}
                            onClick={() => setRefreshNonce((n) => n + 1)}
                            disabled={listLoading || summaryLoading}
                        >
                            <FaSyncAlt
                                className={listLoading || summaryLoading ? styles.refreshSpinning : undefined}
                                aria-hidden="true"
                            />
                            Refresh
                        </button>
                    </div>
                </div>
            </div>

            {listError ? (
                <p className={styles.errorText}>{listError}</p>
            ) : (
                <div className={listLoading ? styles.tableLoading : undefined}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Received</th>
                                <th>Client</th>
                                <th>Email</th>
                                <th>Claims</th>
                                <th>Status</th>
                                <th>UR / Batch</th>
                                <th>Removal</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 ? (
                                <tr>
                                    <td colSpan={8}>
                                        {listLoading ? 'Loading…' : 'No clients match these filters.'}
                                    </td>
                                </tr>
                            ) : (
                                rows.map((row) => (
                                    <tr key={row.client_id}>
                                        <td>{formatDateTime(row.first_received_at)}</td>
                                        <td>
                                            <div>{row.client_name || '—'}</div>
                                            <div className={styles.footnote}>
                                                #{row.client_id}
                                                {row.consumer_client_ref ? ` · ${row.consumer_client_ref}` : ''}
                                            </div>
                                        </td>
                                        <td>{row.email || '—'}</td>
                                        <td>
                                            {row.active_claims}/{row.funder_claim_count}
                                            <div className={styles.footnote}>
                                                introducer {row.introducer_claim_count}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={statusClass(row.status)}>
                                                {LINKED_STATUS_LABELS[row.status]}
                                            </span>
                                        </td>
                                        <td>
                                            {(row.nera_ids || []).join(', ') || '—'}
                                            <div className={styles.footnote}>
                                                {(row.batch_groups || []).slice(0, 2).join(', ') || '—'}
                                                {(row.batch_groups || []).length > 2
                                                    ? ` +${row.batch_groups.length - 2}`
                                                    : ''}
                                            </div>
                                        </td>
                                        <td>
                                            {row.status === 'audit_removed'
                                                ? row.primary_removal_label
                                                : '—'}
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className={styles.secondaryButton}
                                                onClick={() => openClient(row.client_id)}
                                            >
                                                Open
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                    <div className={styles.pagination}>
                        <span>
                            Page {page} of {totalPages} · {total} clients
                        </span>
                        <div>
                            <button
                                type="button"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            <button
                                type="button"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {detailClientId != null && (
                <>
                    <div className={styles.slideOverBackdrop} onClick={closeClient} />
                    <div className={styles.slideOver}>
                        <div className={styles.slideOverHeader}>
                            <div>
                                <h3>
                                    {detailLoading
                                        ? 'Loading…'
                                        : detail?.client?.name || `Client #${detailClientId}`}
                                </h3>
                                {detail?.status && (
                                    <span className={statusClass(detail.status as LinkedFundingStatus)}>
                                        {LINKED_STATUS_LABELS[detail.status as LinkedFundingStatus]}
                                    </span>
                                )}
                            </div>
                            <button type="button" onClick={closeClient}>
                                Close
                            </button>
                        </div>
                        {detail?.error ? (
                            <p className={styles.errorText}>{detail.error}</p>
                        ) : detailLoading ? (
                            <p className={styles.footnote}>Loading detail…</p>
                        ) : detail ? (
                            <div className={styles.linkedDetailBody}>
                                <p className={styles.footnote}>
                                    {detail.client?.email || '—'} · id {detail.client?.id}
                                    {detail.summary?.primary_removal_label &&
                                    detail.status === 'audit_removed'
                                        ? ` · ${detail.summary.primary_removal_label}`
                                        : ''}
                                </p>

                                <h4>Funding batch membership</h4>
                                {(detail.funder_claims || []).length === 0 ? (
                                    <p className={styles.footnote}>No funder_references rows for this client.</p>
                                ) : (
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th>Batch</th>
                                                <th>UR</th>
                                                <th>Case ref</th>
                                                <th>Status</th>
                                                <th>Reason</th>
                                                <th>Removed</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.funder_claims.map((claim: any) => (
                                                <tr key={`${claim.batch_group}-${claim.funder_case_ref}`}>
                                                    <td>{claim.batch_group}</td>
                                                    <td>{claim.nera_id || '—'}</td>
                                                    <td>{claim.introducer_case_ref || claim.funder_case_ref}</td>
                                                    <td>
                                                        {claim.audit_removed ? 'Removed' : 'Active'}
                                                    </td>
                                                    <td>
                                                        {claim.audit_removed
                                                            ? claim.audit_removed_label
                                                            : '—'}
                                                    </td>
                                                    <td>
                                                        {claim.audit_removed_date
                                                            ? formatDateTime(claim.audit_removed_date)
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                <h4>Introducer leads</h4>
                                {(detail.leads || []).length === 0 ? (
                                    <p className={styles.footnote}>No leads.</p>
                                ) : (
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th>Case ref</th>
                                                <th>Lender</th>
                                                <th>Policy</th>
                                                <th>Start</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.leads.map((lead: any) => (
                                                <tr key={lead.case_ref}>
                                                    <td>{lead.case_ref}</td>
                                                    <td>{lead.lender || '—'}</td>
                                                    <td>{lead.policy_number || '—'}</td>
                                                    <td>
                                                        {lead.start_date
                                                            ? formatDateTime(lead.start_date)
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}

                                <h4>API deliveries</h4>
                                {(detail.deliveries || []).length === 0 ? (
                                    <p className={styles.footnote}>No delivery rows.</p>
                                ) : (
                                    <table className={styles.dataTable}>
                                        <thead>
                                            <tr>
                                                <th>Received</th>
                                                <th>Status</th>
                                                <th>Agreements</th>
                                                <th>Delivery id</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detail.deliveries.map((d: any) => (
                                                <tr key={d.id}>
                                                    <td>{formatDateTime(d.created_at)}</td>
                                                    <td>{d.status}</td>
                                                    <td>{d.agreement_count ?? '—'}</td>
                                                    <td className={styles.footnote}>{d.delivery_id}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        ) : null}
                    </div>
                </>
            )}
        </div>
    )
}
